import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, Loading } from "@/components/common";
import { UsersThree, Microphone, Sparkle, ArrowsClockwise, Heart, LockKey, Spinner, Stop,
  UploadSimple, X, Copy, Archive, Trash, PencilSimple, SpeakerHigh, FloppyDisk, FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";

const CONTROLS = ["Face/general appearance", "Hair", "Colour palette", "Clothing inspiration",
  "Body proportions", "Pose", "Expression", "Art style"];

export default function CharacterStudio() {
  const [chars, setChars] = useState(null);
  const [voices, setVoices] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("");
  const [engine, setEngine] = useState("AUTO");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [recording, setRecording] = useState(false);
  const [refs, setRefs] = useState([]); // {id, data_url}
  const [strength, setStrength] = useState("Medium");
  const [controls, setControls] = useState(["Art style", "Colour palette"]);
  const [confirmDel, setConfirmDel] = useState(null); // {char, usage}
  const [talking, setTalking] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  const load = () => api.get("/characters").then(({ data }) => setChars(data));
  useEffect(() => { load(); api.get("/tts/voices").then(({ data }) => setVoices(data.voices)).catch(() => {}); }, []);

  const setVoice = async (c, voice) => {
    try {
      await api.patch(`/characters/${c.id}`, { data: { voice } });
      setChars((cs) => cs.map((x) => x.id === c.id ? { ...x, voice } : x));
      toast.success(`${c.name}'s voice set`);
    } catch { toast.error("Could not set voice"); }
  };
  const previewVoice = async (c, voice) => {
    setPreviewing(c.id);
    try {
      const res = await api.post("/tts/preview", { data: { voice, text: `Hi, I'm ${c.name}!` } }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch { toast.error("Voice preview failed"); }
    finally { setPreviewing(null); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files.slice(0, 4)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads/reference", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRefs((r) => [...r, { id: data.id, data_url: data.data_url }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed");
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleControl = (c) => setControls((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);

  const generate = async () => {
    if (engine === "LOCAL") { toast.error("Studio Local image engine requires a self-hosted endpoint (Settings → Local AI)."); return; }
    if (!prompt.trim() && refs.length === 0) { toast.error("Describe your character or add a reference image"); return; }
    setBusy(true); setImages([]); setSelected(null);
    try {
      const { data } = await api.post("/ai/image", {
        prompt: prompt || "an original character", variations: 4,
        reference_images: refs.map((r) => r.data_url),
        reference_strength: strength, reference_controls: controls,
      });
      setImages(data.images);
      const ok = data.images.filter((i) => i.image).length;
      ok === 0 ? toast.error("Image generation failed.") : toast.success(`Generated ${ok} versions${data.used_references ? " from your reference" : ""}`);
    } catch { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  const mic = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); setRecording(false);
        const fd = new FormData();
        fd.append("file", new Blob(chunksRef.current, { type: "audio/webm" }), "c.webm");
        try { const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPrompt((p) => (p ? p + " " : "") + data.text); }
        catch { toast.error("Transcription failed"); }
      };
      mr.start(); setRecording(true);
    } catch { toast.error("Microphone denied"); }
  };

  const saveGenerated = async (asLead) => {
    if (selected === null || !images[selected]?.image) { toast.error("Select a version first"); return; }
    try {
      const { data } = await api.post("/characters/save-generated", {
        data: {
          image: images[selected].image, as_lead: asLead,
          name: asLead ? (name.trim() || "The Wayfinder") : (name.trim() || "New Character"),
          prompt, params: { reference_strength: strength, reference_controls: controls },
          reference_count: refs.length,
        },
      });
      toast.success(asLead
        ? `Saved as The Wayfinder (v${data.version}) — pending your approval, not yet Official`
        : `Saved as Character (v${data.version})`);
      setImages([]); setSelected(null); setName(""); setPrompt(""); setRefs([]);
      load();
    } catch { toast.error("Could not save"); }
  };

  const lockOfficial = async () => {
    if (selected === null || !images[selected]?.image) { toast.error("Select a version first"); return; }
    try {
      await api.post("/characters", { data: {
        name: name.trim() || "New Character", official: true, image: images[selected].image,
        tagline: prompt.slice(0, 60), personality: prompt, palette: ["#00E5FF"], outfits: ["Default"], relationships: [],
        reference_count: refs.length,
      }});
      toast.success("Locked as official character — saved to Character Bible.");
      setImages([]); setSelected(null); setName(""); setPrompt(""); setRefs([]);
      load();
    } catch { toast.error("Could not save character"); }
  };

  const doDuplicate = async (c) => { await api.post(`/characters/${c.id}/duplicate`); toast.success("Duplicated"); load(); };
  const doArchive = async (c) => { await api.post(`/characters/${c.id}/archive`); toast.success("Archived"); load(); };
  const makeTalking = async (c) => {
    const line = window.prompt(`What should ${c.name} say? (a real lip-synced clip will be generated)`, "Hi everyone! Let's have some fun today!");
    if (!line) return;
    setTalking(c.id);
    try {
      const { data } = await api.post("/media/lipsync", { data: { character_id: c.id, text: line } });
      const jobId = data.job_id; const started = Date.now();
      const poll = async () => {
        const { data: job } = await api.get(`/media/lipsync-job/${jobId}`);
        if (job.status === "done") { setTalking(null); toast.success("Talking clip ready — saved to Asset Library"); window.open(`${API}${job.asset_url}`, "_blank"); }
        else if (job.status === "error") { setTalking(null); toast.error(job.error || "Lip-sync failed"); }
        else if (Date.now() - started > 200000) { setTalking(null); toast.error("Lip-sync timed out"); }
        else setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) { setTalking(null); toast.error(e.response?.data?.detail || "Could not start lip-sync"); }
  };
  const doRename = async (c) => {
    const n = window.prompt("Rename character", c.name);
    if (n && n !== c.name) { await api.patch(`/characters/${c.id}`, { data: { name: n } }); toast.success("Renamed"); load(); }
  };
  const askDelete = async (c) => {
    const { data } = await api.get(`/characters/${c.id}/usage`);
    setConfirmDel({ char: c, usage: data.count, episodes: data.episodes });
  };
  const doDelete = async (force) => {
    const c = confirmDel.char;
    await api.delete(`/characters/${c.id}${force ? "?force=true" : ""}`);
    toast.success(force ? "Deleted (moved to Trash)" : "Moved to Trash");
    setConfirmDel(null); load();
  };

  return (
    <div>
      <PageHeader icon={UsersThree} title="Character Studio"
        subtitle="Create characters from a description or an uploaded reference image. Generate real AI versions, manage them, and lock the official one."
        badge={<StatusBadge status="LIVE" />} />

      <Panel className="mb-6">
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Left: description */}
          <div>
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <input data-testid="char-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Character name"
                className="sm:w-48 rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-white outline-none focus:border-[#00E5FF]/50" />
              <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-white/10 px-3 py-2">
                <span className="text-xs text-zinc-500">Engine</span>
                <select data-testid="char-engine" value={engine} onChange={(e) => setEngine(e.target.value)} className="bg-transparent text-sm text-white outline-none [color-scheme:dark]">
                  <option value="AUTO">Auto</option><option value="CLOUD">Cloud · Gemini</option><option value="LOCAL">Studio Local</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2 text-sm text-zinc-400"><Sparkle size={16} weight="fill" className="text-[#00E5FF]" /> Describe (text or voice)</div>
            <textarea data-testid="char-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
              placeholder="e.g. A small purple alien with three antennas, intelligent but shy. Or describe how to transform the reference image."
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 resize-none" />
            {engine === "LOCAL" && <p className="mt-2 text-xs text-[#FFB800]">Studio Local image engine needs a self-hosted endpoint. Switch to Auto/Cloud to generate now.</p>}
          </div>

          {/* Right: references */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400 flex items-center gap-2"><UploadSimple size={16} weight="duotone" className="text-[#FF007F]" /> Reference images (optional)</span>
              <button data-testid="upload-reference" onClick={() => fileRef.current?.click()} className="text-xs text-[#00E5FF] hover:underline">+ Add image</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="reference-input" />
            {refs.length === 0 ? (
              <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-xl border border-dashed border-white/15 bg-black/20 p-6 text-center hover:border-[#00E5FF]/40 transition-colors">
                <UploadSimple size={26} weight="duotone" className="text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Upload / take a photo — JPG, PNG, WEBP (max 8MB)</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mb-3">
                {refs.map((r, i) => (
                  <div key={r.id} className="relative">
                    <img src={r.data_url} alt="ref" className="h-16 w-16 rounded-lg object-cover border border-white/10" />
                    <span className="absolute -top-1 -left-1 bg-black/70 text-[9px] rounded px-1">REF {i + 1}</span>
                    <button onClick={() => setRefs((rr) => rr.filter((x) => x.id !== r.id))} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-[#FF3366] flex items-center justify-center"><X size={10} weight="bold" /></button>
                  </div>
                ))}
              </div>
            )}
            {refs.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-2 mt-2">
                  <span className="text-xs text-zinc-500">Strength</span>
                  {["Low", "Medium", "High"].map((s) => (
                    <button key={s} data-testid={`strength-${s}`} onClick={() => setStrength(s)} className={`rounded-full px-2.5 py-1 text-xs border ${strength === s ? "border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F]" : "border-white/10 text-zinc-400"}`}>{s}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CONTROLS.map((c) => (
                    <button key={c} onClick={() => toggleControl(c)} className={`rounded-full px-2 py-1 text-[11px] border ${controls.includes(c) ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-zinc-500"}`}>{controls.includes(c) ? "☑ " : ""}{c}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button data-testid="char-generate" onClick={generate} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy ? <Spinner size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />} {busy ? "Generating…" : "Generate A/B/C/D"}
          </button>
          <button data-testid="char-mic" onClick={mic} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 transition-colors ${recording ? "border-[#FF3366] text-[#FF3366] animate-pulseGlow" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
            {recording ? <Stop size={18} weight="fill" /> : <Microphone size={18} weight="duotone" />} {recording ? "Stop" : "Voice"}
          </button>
          <span className="text-xs text-zinc-500">API cost: {engine === "LOCAL" ? "£0.00 self-hosted" : "cloud"}{refs.length ? ` · ${refs.length} ref` : ""}</span>
        </div>
      </Panel>

      {(busy || images.length > 0) && (
        <div className="mb-8">
          <h3 className="font-display text-lg font-bold mb-4">Generated Versions</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(busy ? [0, 1, 2, 3] : images).map((img, i) => {
              const label = busy ? ["Version A", "Version B", "Version C", "Version D"][i] : img.label;
              const isSel = selected === i;
              return (
                <div key={i} data-testid={`char-version-${i}`} onClick={() => !busy && img.image && setSelected(i)}
                  className={`relative rounded-2xl overflow-hidden border cursor-pointer transition-all ${isSel ? "border-[#00E5FF] magic-glow" : "border-white/8"}`}>
                  <div className="aspect-square bg-[#0e0e0e] flex items-center justify-center">
                    {busy ? <Spinner size={26} className="animate-spin text-zinc-600" /> : img.image ? <img src={img.image} alt={label} className="h-full w-full object-cover" /> : <span className="text-xs text-[#FF3366] px-3 text-center">Generation failed</span>}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 scrim p-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-white">{label}</span>
                    {isSel && <Heart size={16} weight="fill" className="text-[#FF007F]" />}
                  </div>
                </div>
              );
            })}
          </div>
          {selected !== null && !busy && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={generate} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-white hover:border-white/30 transition-colors"><ArrowsClockwise size={16} className="text-[#00E5FF]" /> Regenerate</button>
              <button data-testid="char-save" onClick={() => saveGenerated(false)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
                <FloppyDisk size={16} className="text-[#00E5FF]" /> Save as Character
              </button>
              <button data-testid="char-save-lead" onClick={() => saveGenerated(true)} className="inline-flex items-center gap-2 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40 text-[#FF66B3] px-4 py-2 text-sm font-semibold hover:bg-[#FF007F]/25 transition-colors">
                <Heart size={16} weight="fill" className="text-[#FF007F]" /> Save as The Wayfinder
              </button>
              <button data-testid="char-lock" onClick={lockOfficial} className="inline-flex items-center gap-2 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D] px-4 py-2 text-sm font-semibold hover:bg-[#00FF9D]/25 transition-colors">
                <LockKey size={16} weight="fill" /> Lock as Official Character
              </button>
            </div>
          )}
        </div>
      )}

      <h3 className="font-display text-lg font-bold mb-4">Official Characters</h3>
      {!chars ? <Loading /> : chars.length === 0 ? <p className="text-sm text-zinc-500">No characters yet in this studio.</p> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
          {chars.map((c) => (
            <div key={c.id} data-testid={`char-card-${c.id}`} className="relative rounded-2xl overflow-hidden border border-white/8 panel-hover group">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.image} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <IconBtn testid={`char-talk-${c.id}`} icon={talking === c.id ? Spinner : FilmSlate} onClick={() => makeTalking(c)} />
                <IconBtn testid={`char-rename-${c.id}`} icon={PencilSimple} onClick={() => doRename(c)} />
                <IconBtn testid={`char-dup-${c.id}`} icon={Copy} onClick={() => doDuplicate(c)} />
                <IconBtn testid={`char-archive-${c.id}`} icon={Archive} onClick={() => doArchive(c)} />
                <IconBtn testid={`char-delete-${c.id}`} icon={Trash} danger onClick={() => askDelete(c)} />
              </div>
              <div className="absolute inset-x-0 bottom-0 scrim p-4">
                <div className="flex items-center gap-2">
                  <h4 className="font-display text-lg font-bold text-white">{c.name}</h4>
                  {c.official && <StatusBadge status="APPROVED" />}
                </div>
                <p className="text-xs text-zinc-300 mt-0.5">{c.tagline}</p>
                <div className="mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <SpeakerHigh size={14} weight="duotone" className="text-[#00E5FF] shrink-0" />
                  <select data-testid={`char-voice-${c.id}`} value={voices.some(v => v.id === c.voice) ? c.voice : ""}
                    onChange={(e) => setVoice(c, e.target.value)}
                    className="flex-1 min-w-0 rounded-lg bg-black/50 border border-white/15 px-2 py-1 text-[11px] text-white outline-none [color-scheme:dark]">
                    <option value="" disabled>Set voice…</option>
                    {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                  {voices.some(v => v.id === c.voice) && (
                    <button data-testid={`char-voice-preview-${c.id}`} onClick={() => previewVoice(c, c.voice)} disabled={previewing === c.id}
                      className="shrink-0 h-6 w-6 rounded-lg bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center text-[#00E5FF] disabled:opacity-50">
                      {previewing === c.id ? <Spinner size={12} className="animate-spin" /> : <SpeakerHigh size={12} weight="fill" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConfirmDel(null)} />
          <div className="relative w-full max-w-md glass rounded-2xl p-6 animate-fadeUp">
            <h3 className="font-display text-xl font-bold mb-2">Delete "{confirmDel.char.name}"?</h3>
            {confirmDel.usage > 0 ? (
              <p className="text-sm text-[#FFB800] mb-4">This character is currently used in {confirmDel.usage} episode(s): {confirmDel.episodes.join(", ")}. Deleting may affect them.</p>
            ) : <p className="text-sm text-zinc-400 mb-4">This will move the character to Trash. You can restore it later.</p>}
            <div className="flex flex-wrap gap-2">
              <button data-testid="del-cancel" onClick={() => setConfirmDel(null)} className="rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-white">Cancel</button>
              <button data-testid="del-archive" onClick={() => { doArchive(confirmDel.char); setConfirmDel(null); }} className="rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-[#00E5FF]">Archive instead</button>
              <button data-testid="del-confirm" onClick={() => doDelete(confirmDel.usage > 0)} className="rounded-full bg-[#FF3366]/15 border border-[#FF3366]/40 text-[#FF3366] px-4 py-2 text-sm font-semibold">{confirmDel.usage > 0 ? "Delete anyway" : "Move to Trash"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`h-7 w-7 rounded-lg glass flex items-center justify-center ${danger ? "text-[#FF3366]" : "text-white"} hover:brightness-125`}>
      <Icon size={14} weight="bold" />
    </button>
  );
}
